import React from "react";
import { Outlet } from "react-router-dom";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import {
  SubNavItem,
  usePageHeader,
  useSubNav,
} from "../../context/LayoutContext";
import {
  useAdminDashboardEnabled,
  useInternalAffairsEnabled,
} from "./featureFlag";

// Section tabs rendered by the TopAppBar (KardaAdminLayout precedent).
// useSubNav must live HERE, not in a tab: it clears the items on unmount, so a
// tab-owned registration would blank the tabs during navigation.
const OVERVIEW_ITEM: SubNavItem = {
  label: "Overview",
  path: "/station-admin",
  icon: <DashboardOutlinedIcon fontSize="small" />,
};
const INTERNAL_AFFAIRS_ITEM: SubNavItem = {
  label: "Internal Affairs",
  path: "/station-admin/internal-affairs",
  icon: <PolicyOutlinedIcon fontSize="small" />,
};

/**
 * Chrome for the Station Admin area. Sets the TopAppBar header and renders the
 * nested dashboard via <Outlet />. Kept as a thin layout (mirroring
 * KardaAdminLayout) so future sub-sections can be added as sibling routes.
 */
const StationAdminLayout: React.FC = () => {
  // Each tab has its own per-station flag: Overview → `admin-dashboard`,
  // Internal Affairs → `internal-affairs`. A tab appears only when its flag is
  // on, and the tab bar shows only when there's more than one destination to
  // switch between — a lone tab is pointless chrome. The header subtitle
  // likewise names only what's available. useSubNav re-registers when this
  // array's content changes, so tabs appear once flags resolve on a hard
  // refresh. (RequireStationAdminArea guarantees at least one flag is on.)
  const dashboardEnabled = useAdminDashboardEnabled();
  const iaEnabled = useInternalAffairsEnabled();

  usePageHeader({
    title: "Station Admin",
    subtitle:
      dashboardEnabled && iaEnabled
        ? "Your station's activity and compliance"
        : iaEnabled
          ? "Your station's compliance"
          : "Your station's activity",
    icon: <ShieldOutlinedIcon />,
  });

  const navItems = React.useMemo(() => {
    const tabs: SubNavItem[] = [];
    if (dashboardEnabled) tabs.push(OVERVIEW_ITEM);
    if (iaEnabled) tabs.push(INTERNAL_AFFAIRS_ITEM);
    return tabs.length > 1 ? tabs : [];
  }, [dashboardEnabled, iaEnabled]);
  useSubNav(navItems);

  return <Outlet />;
};

export default StationAdminLayout;
